import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentDto,
  DocumentUpdateRequest,
  FileParameter,
} from 'src/api/FiretrolApiClient';
import { documentApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { queryKeys } from 'src/api/queries/queryKeys';

export function useUploadDocument(jobId: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: FileParameter) =>
      documentApiClient.uploadFile(jobId!, file),
    onSettled: () => invalidateFor('document.write', qc, { jobId: jobId! }),
  });
}

export function useUploadDocumentToFolder(jobId: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      folderId,
      file,
    }: {
      folderId: string;
      file: FileParameter;
    }) => documentApiClient.uploadFileToFolder(jobId!, folderId, file),
    onSettled: () => invalidateFor('document.write', qc, { jobId: jobId! }),
  });
}

export function useUploadDocumentVersion(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      file,
    }: {
      documentId: string;
      file: FileParameter;
    }) => documentApiClient.uploadDocumentVersion(jobId, documentId, file),
    meta: { successMessage: 'Version uploaded!' },
    onSuccess: (document: DocumentDto) =>
      qc.setQueryData(queryKeys.documents.detail(jobId, document.id), document),
    onSettled: () => invalidateFor('document.write', qc, { jobId }),
  });
}

export function useUpdateDocument(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      request,
    }: {
      documentId: string;
      request: DocumentUpdateRequest;
    }) => documentApiClient.updateDocument(jobId, documentId, request),
    meta: { successMessage: 'Document updated!' },
    onSettled: () => invalidateFor('document.write', qc, { jobId }),
  });
}

export function useDeleteDocument(jobId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      documentApiClient.removeDocument(jobId, documentId),
    meta: { successMessage: 'Document removed!' },
    onSettled: () => invalidateFor('document.write', qc, { jobId }),
  });
}
